<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    loading-message="Loading Number of Rides Taken Per User..."
    button-location="top"
    @submit="fetchNumberOfRidesTakenPerUser()"
  >
    <template #title>
      Number of Rides Taken Per User
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Customer Name </label>
        <div class="col-sm-6">
          <sanitized-input
            v-model="searchConditions.callerName"
            placeholder="Please input customer name"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Status </label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="searchConditions.status"
            :options="statusTypeList"
          >
            <template #additional-options-prefix>
              <el-option
                label="All Status"
                value=""
              >
                All Status
              </el-option>
            </template>
          </dropdown-list>
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Time Span </label>
        <div class="col-sm-6">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>
      </div>

      <div class="form-submit-button-group">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="isLoading"
        >
          Search
        </p-button>

        <p-button
          type="primary"
          round
          :disabled="isLoading"
          @click="downloadCSV"
        >
          Download
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <message-html :result-html="errorHtml" />
    </template>

    <template #content>
      <pagination
        class="mt-20 mb-10"
        :is-loading="isLoading"
        :current-page="paginationInfo.currentPage"
        :total-pages="paginationInfo.totalPages"
        @load-page-data="fetchNumberOfRidesTakenPerUser"
      />

      <table-view-container
        :page-id="pageId"
        :data="tableData"
        :table-columns="tableColumns"
      />
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, reactive, watch, onMounted } from 'vue';

import util from '@/Utils/util';
import { usePartner } from '@/use/partner';
import { TableColumnType } from '@/models/table';
import { ListType } from '@/models/app';
import { NumberOfRidesPerUser } from '@/models/ride';
import { rideService } from '@/services/ride';

const pageId = 'number-of-rides-taken-per-user';
const statusTypeList: ListType[] = [
  { id: 1, key: 'start', label: 'Start' },
  { id: 2, key: 'processing', label: 'Processing' },
  { id: 3, key: 'accepted', label: 'Accepted' },
  { id: 4, key: 'arriving', label: 'Arriving' },
  { id: 5, key: 'in_progress', label: 'In Progress' },
  { id: 6, key: 'completed', label: 'Completed' },
  { id: 7, key: 'rider_canceled', label: 'Rider Canceled' },
  { id: 8, key: 'operator_canceled', label: 'Operator Canceled' },
  { id: 9, key: 'driver_canceled', label: 'Driver Canceled' },
  { id: 10, key: 'driver_asked_to_cancel', label: 'Driver Asked to Cancel' },
  { id: 11, key: 'no_drivers_available', label: 'No Drivers Available' },
  { id: 12, key: 'car_too_big', label: 'Car Too Big' },
  { id: 13, key: 'bad_driver', label: 'Bad Driver' },
];
const tableColumns: TableColumnType[] = [
  { label: 'No', field: 'index', width: 70, isCenterAlign: true },
  { label: 'Caller ID', field: 'callerId', width: 140 },
  { label: 'Customer Name', field: 'callerName', width: 140 },
  { label: 'Phone Number', field: 'phoneNumber', width: 140 },
  { label: 'Member Address', field: 'homeAddress', width: 180 },
  { label: 'Number of Rides', field: 'numberOfRides', width: 140 },
  { label: 'Partner Credits', field: 'partnerCredits', width: 140, formatter: util.formatAmountWithCurrency },
  { label: 'Partner Cost', field: 'partnerCosts', width: 140, formatter: util.formatAmountWithCurrency },
  { label: 'Partner Total', field: 'partnerTotal', width: 140, formatter: util.formatAmountWithCurrency },
];
const tablePaginationLimit: number = 10;

export default {
  name: 'NumberOfRidesTakenPerUser',

  setup: () => {
    const { partnerId } = usePartner();

    const timeSpanValue: Ref<any> = ref(null);
    const searchConditions = reactive({
      callerName: '',
      status: '',
      startDateTime: '',
      endDateTime: '',
    });
    const paginationInfo = reactive({
      totalPages: 1,
      currentPage: 1,
    });
    const isLoading = ref(false);
    const errorHtml = ref('');
    const tableData: Ref<NumberOfRidesPerUser[]> = ref([]);

    const fetchNumberOfRidesTakenPerUser = async (currentPage = 1) => {
      paginationInfo.currentPage = currentPage;
      errorHtml.value = '';
      isLoading.value = true;

      try {
        const offset = (currentPage - 1) * tablePaginationLimit;
        const response = await rideService.getNumberOfRidesPerUser({
          callerName: searchConditions.callerName,
          status: searchConditions.status,
          startDate: searchConditions.startDateTime,
          endDate: searchConditions.endDateTime,
          partnerId: partnerId.value,
          pagination: {
            limit: tablePaginationLimit,
            offset,
          },
        });

        paginationInfo.totalPages = Math.ceil(response.count / tablePaginationLimit) || 1;
        tableData.value = response.rows.map((item: NumberOfRidesPerUser, index: number) => {
          return {
            ...item,
            index: offset + index + 1,
          };
        });
      } catch (error) {
        tableData.value = [];
        errorHtml.value = util.getGraphqlError(error);
      }
      isLoading.value = false;
    };

    const resetForm = () => {
      tableData.value = [];
      errorHtml.value = '';
      paginationInfo.totalPages = 1;
      paginationInfo.currentPage = 1;
      searchConditions.callerName = '';
      searchConditions.status = '';
      timeSpanValue.value = null;
    };

    const downloadCSV = async () => {
      errorHtml.value = '';
      isLoading.value = true;

      try {
        const response = await rideService.downloadNumberOfRidesTakenPerUser({
          callerName: searchConditions.callerName,
          status: searchConditions.status,
          startDate: searchConditions.startDateTime,
          endDate: searchConditions.endDateTime,
          partnerId: partnerId.value,
        });

        util.downloadJSONToCSV(response, 'rides_taken_per_user.csv');
      } catch (error) {
        errorHtml.value = util.getGraphqlError(error);
      }

      isLoading.value = false;
    };

    watch(timeSpanValue, (value: any) => {
      if (value) {
        searchConditions.startDateTime = util.formatDateTime(value[0], 'YYYY-MM-DD');
        searchConditions.endDateTime = util.formatDateTime(value[1], 'YYYY-MM-DD');
      } else {
        searchConditions.startDateTime = '';
        searchConditions.endDateTime = '';
      }
    });

    onMounted(fetchNumberOfRidesTakenPerUser);

    return {
      pageId,
      statusTypeList,
      isLoading,
      paginationInfo,
      searchConditions,
      timeSpanValue,
      errorHtml,
      tableData,
      tableColumns,

      fetchNumberOfRidesTakenPerUser,
      resetForm,
      downloadCSV,
    };
  },
};
</script>
